<div class="animate-fadeIn">
  <div class="flex flex-col gap-8 pb-32">
    <h3 class="text-xs font-black uppercase tracking-[0.2em] text-slate-500">{{ 'imaging_settings_title' | translate }}</h3>
    <div class="bg-white rounded-[2.5rem] p-8 border border-slate-100 shadow-sm flex flex-col gap-8">
      <p class="text-sm text-slate-500 leading-relaxed">{{ 'imaging_settings_hint' | translate }}</p>

      <!-- Browser: agent check is user-initiated (avoids the Local Network
           Access prompt firing just because the tab was opened) -->
      <div *ngIf="probeDeferred" class="flex flex-col sm:flex-row sm:items-center gap-4">
        <p class="text-sm text-slate-500 flex-1">{{ 'imaging_probe_deferred_hint' | translate }}</p>
        <app-button
          [label]="'imaging_check_again' | translate"
          variant="secondary"
          (onClick)="loadImaging()">
        </app-button>
      </div>

      <!-- Status chips -->
      <div class="flex flex-wrap items-center gap-3" *ngIf="!imagingLoading && imagingStatus">
        <span class="text-[10px] font-black uppercase tracking-widest px-3 py-1 rounded-full border"
          [ngClass]="imagingStatus.reachable ? 'text-emerald-600 bg-emerald-50 border-emerald-100' : 'text-rose-600 bg-rose-50 border-rose-100'">
          {{ (imagingStatus.reachable ? 'imaging_status_agent_running' : 'imaging_status_agent_not_running') | translate }}
        </span>
        <span *ngIf="imagingStatus.reachable" class="text-[10px] font-black uppercase tracking-widest px-3 py-1 rounded-full border"
          [ngClass]="imagingStatus.supported ? 'text-emerald-600 bg-emerald-50 border-emerald-100' : 'text-amber-600 bg-amber-50 border-amber-100'">
          {{ (imagingStatus.supported ? 'imaging_status_supported' : 'imaging_status_unsupported') | translate }}
        </span>
        <span *ngIf="imagingStatus.reachable" class="text-[10px] font-black uppercase tracking-widest px-3 py-1 rounded-full border"
          [ngClass]="imagingStatus.configured ? 'text-emerald-600 bg-emerald-50 border-emerald-100' : 'text-amber-600 bg-amber-50 border-amber-100'">
          {{ (imagingStatus.configured ? 'imaging_status_configured' : 'imaging_status_not_configured') | translate }}
        </span>
        <button (click)="loadImaging()" class="ml-auto p-2 text-slate-400 hover:text-primary hover:bg-primary/5 rounded-xl transition-all" [title]="'imaging_check_again' | translate">
          <svg class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" /></svg>
        </button>
      </div>
      <p *ngIf="imagingLoading" class="text-[10px] text-slate-400 font-black uppercase tracking-widest">{{ 'common_loading' | translate }}</p>

      <!-- Agent not running: point at the installers in the Executables tab -->
      <p *ngIf="!imagingLoading && imagingStatus && !imagingStatus.reachable" class="text-sm text-slate-500">
        {{ 'imaging_agent_install_hint' | translate }}
      </p>

      <!-- Carestream config form -->
      <div *ngIf="!imagingLoading && imagingStatus?.reachable && imagingConfig && vendorShown('carestream')" class="flex flex-col gap-8">
        <div class="flex items-center gap-3">
          <h4 class="text-[10px] font-black uppercase tracking-widest text-slate-700">{{ 'imaging_vendor_carestream' | translate }}</h4>
          <span class="text-[10px] font-black uppercase tracking-widest px-3 py-1 rounded-full border"
            [ngClass]="vendorConfigured('carestream') ? 'text-emerald-600 bg-emerald-50 border-emerald-100' : 'text-amber-600 bg-amber-50 border-amber-100'">
            {{ (vendorConfigured('carestream') ? 'imaging_status_configured' : 'imaging_status_not_configured') | translate }}
          </span>
        </div>
        <div class="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <div class="flex flex-col gap-2">
            <label class="text-[10px] font-black uppercase tracking-widest text-slate-500">{{ 'imaging_field_executable' | translate }}</label>
            <input [(ngModel)]="imagingForm.executablePath" [disabled]="!canEdit" type="text" pInputText
              [placeholder]="'imaging_field_executable_placeholder' | translate"
              class="w-full !bg-slate-50 !border-slate-100 !rounded-2xl !text-sm !font-mono focus:!ring-4 focus:!ring-primary/10 focus:!border-primary !transition-all">
          </div>
          <div class="flex flex-col gap-2">
            <label class="text-[10px] font-black uppercase tracking-widest text-slate-500">{{ 'imaging_field_repository' | translate }}</label>
            <input [(ngModel)]="imagingForm.patientRepository" [disabled]="!canEdit" type="text" pInputText
              [placeholder]="'imaging_field_repository_placeholder' | translate"
              class="w-full !bg-slate-50 !border-slate-100 !rounded-2xl !text-sm !font-mono focus:!ring-4 focus:!ring-primary/10 focus:!border-primary !transition-all">
          </div>
          <div class="flex flex-col gap-2">
            <label class="text-[10px] font-black uppercase tracking-widest text-slate-500">{{ 'imaging_field_format' | translate }}</label>
            <select [(ngModel)]="imagingForm.databaseFormat" [disabled]="!canEdit"
              class="w-full bg-slate-50 border border-slate-100 rounded-2xl text-sm px-4 py-3 focus:ring-4 focus:ring-primary/10 focus:border-primary transition-all">
              <option value="" disabled>{{ 'imaging_field_format_placeholder' | translate }}</option>
              <option value="urx-patient-id">{{ 'imaging_format_urx_patient_id' | translate }}</option>
              <option value="trophy-urx">{{ 'imaging_format_trophy_urx' | translate }}</option>
              <option value="legacy-trophy">{{ 'imaging_format_legacy_trophy' | translate }}</option>
              <!-- 'patient-id' (one flat folder per patient) is a real CS Imaging
                   layout the agent still accepts, but new setups should not pick
                   it (new clinics use urx-patient-id). Shown DISABLED so a machine
                   already configured with it displays its true value instead of a
                   blank select beside a green "configured" chip - an Angular
                   select with no matching option renders empty, and a user
                   "fixing" that would re-point folder resolution for the whole
                   clinic in one save. -->
              <option value="patient-id" disabled>{{ 'imaging_format_patient_id' | translate }}</option>
            </select>
          </div>
        </div>
        <div class="flex justify-end" *hasPermission="'Configurações:edit'">
          <app-button
            [label]="(imagingSaving ? 'common_loading' : 'common_save_changes') | translate"
            icon="pi pi-save"
            variant="primary"
            [disabled]="!imagingDirty || imagingSaving"
            (onClick)="saveImagingConfig()">
          </app-button>
        </div>
      </div>

      <!-- SIDEXIS config form. Separate section and separate save: a clinic PC
           can have both vendors installed, and configuring one must never touch
           the other's settings. -->
      <div *ngIf="!imagingLoading && imagingStatus?.reachable && sidexisConfig && vendorShown('sidexis')" class="flex flex-col gap-8 pt-8 border-t border-slate-100">
        <div class="flex items-center gap-3">
          <h4 class="text-[10px] font-black uppercase tracking-widest text-slate-700">{{ 'imaging_vendor_sidexis' | translate }}</h4>
          <span class="text-[10px] font-black uppercase tracking-widest px-3 py-1 rounded-full border"
            [ngClass]="vendorConfigured('sidexis') ? 'text-emerald-600 bg-emerald-50 border-emerald-100' : 'text-amber-600 bg-amber-50 border-amber-100'">
            {{ (vendorConfigured('sidexis') ? 'imaging_status_configured' : 'imaging_status_not_configured') | translate }}
          </span>
        </div>
        <p class="text-sm text-slate-500 leading-relaxed">{{ 'imaging_sidexis_hint' | translate }}</p>
        <div class="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <div class="flex flex-col gap-2">
            <label class="text-[10px] font-black uppercase tracking-widest text-slate-500">{{ 'imaging_field_sidexis_executable' | translate }}</label>
            <input [(ngModel)]="sidexisForm.executablePath" [disabled]="!canEdit" type="text" pInputText
              [placeholder]="'imaging_field_sidexis_executable_placeholder' | translate"
              class="w-full !bg-slate-50 !border-slate-100 !rounded-2xl !text-sm !font-mono focus:!ring-4 focus:!ring-primary/10 focus:!border-primary !transition-all">
          </div>
          <div class="flex flex-col gap-2">
            <label class="text-[10px] font-black uppercase tracking-widest text-slate-500">{{ 'imaging_field_mailbox' | translate }}</label>
            <input [(ngModel)]="sidexisForm.mailboxPath" [disabled]="!canEdit" type="text" pInputText
              [placeholder]="'imaging_field_mailbox_placeholder' | translate"
              class="w-full !bg-slate-50 !border-slate-100 !rounded-2xl !text-sm !font-mono focus:!ring-4 focus:!ring-primary/10 focus:!border-primary !transition-all">
          </div>
          <div class="flex flex-col gap-2">
            <label class="text-[10px] font-black uppercase tracking-widest text-slate-500">{{ 'imaging_field_station' | translate }}</label>
            <input [(ngModel)]="sidexisForm.stationName" [disabled]="!canEdit" type="text" pInputText
              [placeholder]="'imaging_field_station_placeholder' | translate"
              class="w-full !bg-slate-50 !border-slate-100 !rounded-2xl !text-sm !font-mono focus:!ring-4 focus:!ring-primary/10 focus:!border-primary !transition-all">
          </div>
          <div class="flex flex-col gap-2">
            <label class="text-[10px] font-black uppercase tracking-widest text-slate-500">{{ 'imaging_field_id_source' | translate }}</label>
            <select [(ngModel)]="sidexisForm.patientIdSource" [disabled]="!canEdit"
              class="w-full bg-slate-50 border border-slate-100 rounded-2xl text-sm px-4 py-3 focus:ring-4 focus:ring-primary/10 focus:border-primary transition-all">
              <option value="" disabled>{{ 'imaging_field_id_source_placeholder' | translate }}</option>
              <option value="external-id">{{ 'imaging_id_source_external' | translate }}</option>
              <option value="codigo">{{ 'imaging_id_source_codigo' | translate }}</option>
            </select>
          </div>
          <div class="flex flex-col gap-2 lg:col-span-2">
            <label class="text-[10px] font-black uppercase tracking-widest text-slate-500">{{ 'imaging_field_ini' | translate }}</label>
            <input [(ngModel)]="sidexisForm.sironaIniPath" [disabled]="!canEdit" type="text" pInputText
              [placeholder]="'imaging_field_ini_placeholder' | translate"
              class="w-full !bg-slate-50 !border-slate-100 !rounded-2xl !text-sm !font-mono focus:!ring-4 focus:!ring-primary/10 focus:!border-primary !transition-all">
            <p class="text-xs text-slate-400">{{ 'imaging_field_ini_hint' | translate }}</p>
          </div>
        </div>
        <div class="flex justify-end" *hasPermission="'Configurações:edit'">
          <app-button
            [label]="(sidexisSaving ? 'common_loading' : 'common_save_changes') | translate"
            icon="pi pi-save"
            variant="primary"
            [disabled]="!sidexisDirty || sidexisSaving"
            (onClick)="saveSidexisConfig()">
          </app-button>
        </div>
      </div>

      <!-- Imaging systems this computer does not use. Kept out of the way as
           buttons rather than shown as empty forms, since most clinics run one
           system and the rest is noise to them. -->
      <ng-container *hasPermission="'Configurações:edit'">
        <div *ngIf="!imagingLoading && imagingStatus?.reachable && addableVendors.length"
          class="flex flex-col sm:flex-row sm:items-center gap-4 pt-8 border-t border-slate-100">
          <p class="text-sm text-slate-500 flex-1">{{ addVendorHintKey | translate }}</p>
          <div class="flex flex-wrap gap-3">
            <button *ngFor="let vendor of addableVendors" (click)="revealVendor(vendor)"
              class="px-4 py-2 rounded-2xl border border-slate-200 text-[10px] font-black uppercase tracking-widest text-slate-600 hover:border-primary hover:text-primary transition-all flex items-center gap-2">
              <i class="pi pi-plus text-[10px]"></i>
              {{ 'imaging_vendor_' + vendor | translate }}
            </button>
          </div>
        </div>
      </ng-container>
    </div>
  </div>
</div>
